import { Dispatch, SetStateAction } from "react";
import { Dayjs } from "dayjs";
import "dayjs/locale/th";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  STATUS_FILTER,
  RISK_VIEW_FILTER,
  RiskView,
  CreatedByOption,
} from "./data";

interface ReprintAuthFilterProps {
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  status: string;
  setStatus: Dispatch<SetStateAction<string>>;
  routeCode: string;
  setRouteCode: Dispatch<SetStateAction<string>>;
  createdBy: string;
  setCreatedBy: Dispatch<SetStateAction<string>>;
  dateFrom: Dayjs | null;
  setDateFrom: Dispatch<SetStateAction<Dayjs | null>>;
  dateTo: Dayjs | null;
  setDateTo: Dispatch<SetStateAction<Dayjs | null>>;
  riskView: RiskView;
  setRiskView: Dispatch<SetStateAction<RiskView>>;
  routeOptions: string[];
  createdByOptions: CreatedByOption[];
}

interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
}

const FilterField = ({ label, children }: FilterFieldProps) => (
  <Grid size={{ xs: 12, sm: 4 }}>
    <Grid container alignItems="center" spacing={1}>
      <Grid size={4}>
        <Typography variant="body2" noWrap>
          {label}
        </Typography>
      </Grid>
      <Grid size={8}>{children}</Grid>
    </Grid>
  </Grid>
);

export default function ReprintAuthFilter({
  search,
  setSearch,
  status,
  setStatus,
  routeCode,
  setRouteCode,
  createdBy,
  setCreatedBy,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  riskView,
  setRiskView,
  routeOptions,
  createdByOptions,
}: ReprintAuthFilterProps) {
  return (
    <Grid container spacing={2} mb={1}>
      <FilterField label="ค้นหา">
        <TextField
          size="small"
          fullWidth
          placeholder="เลขที่บิล / ชื่อลูกค้า / รหัสลูกค้า"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </FilterField>
      <FilterField label="สถานะ">
        <TextField
          select
          size="small"
          fullWidth
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {STATUS_FILTER.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="สายส่ง">
        <TextField
          select
          size="small"
          fullWidth
          value={routeCode}
          onChange={(e) => setRouteCode(e.target.value)}
        >
          <MenuItem value="all">ทั้งหมด</MenuItem>
          {routeOptions.map((code) => (
            <MenuItem key={code} value={code}>
              {code}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="สร้างโดย">
        <TextField
          select
          size="small"
          fullWidth
          value={createdBy}
          onChange={(e) => setCreatedBy(e.target.value)}
        >
          <MenuItem value="all">ทั้งหมด</MenuItem>
          {createdByOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
      <FilterField label="วันที่เริ่ม">
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
          <DatePicker
            value={dateFrom}
            format="DD/MM/YYYY"
            onChange={(value) => setDateFrom(value)}
            slotProps={{
              textField: { size: "small", fullWidth: true },
              field: { clearable: true },
            }}
          />
        </LocalizationProvider>
      </FilterField>
      <FilterField label="วันที่สิ้นสุด">
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
          <DatePicker
            value={dateTo}
            format="DD/MM/YYYY"
            minDate={dateFrom ?? undefined}
            onChange={(value) => setDateTo(value)}
            slotProps={{
              textField: { size: "small", fullWidth: true },
              field: { clearable: true },
            }}
          />
        </LocalizationProvider>
      </FilterField>
      <FilterField label="มุมมองเสี่ยง">
        <TextField
          select
          size="small"
          fullWidth
          value={riskView}
          onChange={(e) => setRiskView(e.target.value as RiskView)}
        >
          {RISK_VIEW_FILTER.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
    </Grid>
  );
}
